import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './email.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { stratum } from '../../../../../constants/stratum.js';

class RefiPersonalEmailComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepCommunication');
    this.applicant = {};
    this.coAppPrefix = '';
    this.confirmEmailId = '';
    this.emailId = '';
    this.emailsMatch = true;
    this.values = {};
    this.iconicV2 = ApplicationService.isIconicV2;
  }

  static styles = [style, globalStyle];

  static properties = {
    applicant: { type: Object, reflect: true },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    emailsMatch: { type: Boolean, reflect: true },
    showConfirm: { type: Boolean, reflect: true },
    values: { type: Object, reflect: true },
  };

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.emailId = `${this.coAppPrefix}emailAddress`;
    this.confirmEmailId = `${this.coAppPrefix}confirmEmailAddress`;
    this.application = ApplicationService.Application;
    this.applicant = this.isPrimary
      ? this.application.primaryApplicant
      : this.application.coApplicant;
    const emailChanged =
      (this.isPrimary && ApplicationService.getPrimaryApplicantEmailChanged) ||
      (!this.isPrimary && ApplicationService.getCoApplicantEmailChanged);

    this.values = { emailAddress: this.applicant.emailAddress };
    this.showConfirm =
      this.values.emailAddress === '' || !this.isPrefill || emailChanged;
    if (!emailChanged) {
      this.values.confirmEmailAddress = '';
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('showConfirm')) {
      if (this.showConfirm) {
        const confirmEmailField = this.shadowRoot.getElementById(
          this.confirmEmailId
        );
        confirmEmailField.onpaste = (event) => event.preventDefault();
      }
    }
  }

  render() {
    const errorMessage = !this.emailsMatch
      ? 'Please make sure email addresses match'
      : nothing;

    return html` <div>
      <div class="personal__email">
        <fs-text-field
          id="${this.emailId}"
          autocomplete="email"
          name="email"
          type="email"
          value-missing-message="Please enter a valid email address"
          maxLength="250"
          pattern="(.*)@(.*)\\..{1,}"
          required
          @blur="${(ev) => {
            this.changed('emailAddress', ev);
            if (ev.target.validity.valid) {
              stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                replacements: { value: this.emailId },
              });
            } else {
              stratum.publishTag(
                TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                {
                  replacements: { value: 'Please enter a valid email address' },
                }
              );
            }
          }}"
          @change="${(ev) => {
            if (this.isPrimary) {
              ApplicationService.primaryApplicantEmailChanged = true;
            } else {
              ApplicationService.coApplicantEmailChanged = true;
            }
            this.changed('emailAddress', ev);
          }}"
          value="${this.values.emailAddress}"
          pattern-mismatch-message="Please enter a valid email address"
          autofocus
        >
          Email address
        </fs-text-field>
      </div>

      ${this.showConfirm
        ? html`<div class="personal__confirm-email">
            <fs-text-field
              class="includes-error-message"
              id="${this.confirmEmailId}"
              name="confirm.email"
              type="email"
              value-missing-message="Please enter a valid email address"
              maxLength="250"
              pattern="(.*)@(.*)\\..{1,}"
              required
              @change="${(ev) => {
                this.changed('confirmEmailAddress', ev);
              }}"
              value="${this.values.confirmEmailAddress}"
              pattern-mismatch-message="Please enter a valid email address"
              error-message="${errorMessage}"
              helper-text="${this.iconicV2
                ? 'When you provide your email address, we may use it to send you important information about your application and account(s), as well as other useful products and services.'
                : ''}"
              @blur="${(ev) => {
                this.changed('confirmEmailAddress', ev);
                if (ev.target.validity.valid) {
                  stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                    replacements: { value: this.confirmEmailId },
                  });
                } else {
                  stratum.publishTag(
                    TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                    {
                      replacements: {
                        value: 'Please enter a valid email address',
                      },
                    }
                  );
                }
              }}"
            >
              Confirm email address
            </fs-text-field>
          </div>`
        : nothing}
      <fs-body-text size="small" class="callout" ?hidden="${this.iconicV2}">
        When you provide your email address, we may use it to send you important
        information about your application and account(s), as well as other
        useful products and services.
      </fs-body-text>
    </div>`;
  }

  resetValues() {
    this.application = ApplicationService.Application;
    this.applicant = this.isPrimary
      ? this.application.primaryApplicant
      : this.application.coApplicant;
    this.values = {
      emailAddress: this.applicant.emailAddress,
      confirmEmailAddress: '',
    };
    this.emailsMatch = true;
    this.showConfirm = false;
    // Ensure DOM is settled before grabbing the element
    setTimeout(() => {
      const emailField = this.shadowRoot.getElementById(this.emailId);
      emailField.setAttribute('value', this.values.emailAddress);
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'emailAddress',
            isValid: emailField.validity.valid && emailField.value !== '',
          },
        })
      );
      if (emailField.value !== '') {
        this.values.emailAddress = emailField.value;
      }

      const compareEmailField = this.shadowRoot.getElementById(
        this.confirmEmailId
      );
      if (compareEmailField) {
        compareEmailField.setAttribute('value', '');
      }

      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'confirmEmailAddress',
            isValid: true,
          },
        })
      );
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'compareEmails',
            isValid: true,
          },
        })
      );
      this.requestUpdate();
    }, 0);
  }

  firstUpdated() {
    const emailField = this.shadowRoot.getElementById(this.emailId);
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: 'emailAddress',
          isValid: emailField.validity.valid && emailField.value !== '',
        },
      })
    );
    if (emailField.value !== '') {
      this.values.emailAddress = emailField.value;
    }
    if (this.showConfirm) {
      const confirmEmailField = this.shadowRoot.getElementById(
        this.confirmEmailId
      );
      confirmEmailField.onpaste = (event) => event.preventDefault();
      if (confirmEmailField.value !== '') {
        this.values.confirmEmailAddress = confirmEmailField.value;
      }
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'confirmEmailAddress',
            isValid:
              confirmEmailField.validity.valid &&
              confirmEmailField.value !== '',
          },
        })
      );
    }
  }

  runBusinessChecks() {
    this.compareEmails();
  }

  changed(name, ev) {
    const { target } = ev;
    setTimeout(() => {
      this.showConfirm = true;
      this.values[name] = target.value;

      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: { name, isValid: target.validity.valid },
        })
      );
      this.compareEmails();
    }, 0);
  }

  compareEmails() {
    // if we need to confirm, compare the emails and bubble up the validity result
    if (
      this.showConfirm &&
      this.values.emailAddress &&
      !this.values.confirmEmailAddress
    ) {
      this.emailsMatch = false;
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'compareEmails',
            isValid: this.emailsMatch,
          },
        })
      );
      this.requestUpdate();
      return;
    }
    if (
      this.showConfirm &&
      this.values.emailAddress &&
      this.values.confirmEmailAddress
    ) {
      const emailField = this.shadowRoot.getElementById(this.emailId);
      const confirmEmailField = this.shadowRoot.getElementById(
        this.confirmEmailId
      );

      const emailValue = emailField?.value?.trim().toLowerCase() ?? '';
      const confirmEmailValue =
        confirmEmailField?.value?.trim().toLowerCase() ?? '';

      this.emailsMatch = emailValue === confirmEmailValue;
      if (!this.emailsMatch) {
        stratum.publishTag(
          TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
          { replacements: { value: 'Please make sure email addresses match' } }
        );
      }
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'compareEmails',
            isValid: this.emailsMatch,
          },
        })
      );
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'confirmEmailAddress',
            isValid: this.emailsMatch,
          },
        })
      );
    }
    this.requestUpdate();
  }
}

customElements.define('refi-personal-email', RefiPersonalEmailComponent);
